import type { RunRuntime } from "../types";

/** Plain names for what the worker reports (`footfall/runtime.py`). */

const ACCELERATOR: Record<string, string> = { cuda: "CUDA", mps: "Apple Metal", cpu: "CPU" };

const HWACCEL: Record<string, string> = {
  cuda: "NVDEC (NVIDIA)",
  d3d11va: "D3D11VA",
  dxva2: "DXVA2",
  qsv: "Quick Sync",
  vaapi: "VA-API",
  videotoolbox: "VideoToolbox",
};

const PROVIDER: Record<string, string> = {
  CUDAExecutionProvider: "CUDA",
  CoreMLExecutionProvider: "CoreML",
  CPUExecutionProvider: "CPU",
};

export interface Backend {
  /** Short name: "CUDA", "CPU", "NVDEC (NVIDIA)", "off". */
  label: string;
  /** On an accelerator (GPU/NPU) rather than the CPU. */
  gpu: boolean;
}

export const isOpenVino = (modelId: string | null | undefined) =>
  !!modelId && modelId.endsWith("_openvino_model");

/** Where person detection runs. `modelId` is the detector a run will use
 *  (an OpenVINO export runs on its own plugin, not the torch device). */
export function detectionBackend(rt: RunRuntime, modelId?: string | null): Backend {
  const model = modelId || rt.detector;
  if (isOpenVino(model)) {
    const plugin = rt.detector_backend?.startsWith("OpenVINO")
      ? rt.detector_backend.replace("OpenVINO ", "")
      : null;
    // "intel:gpu" → "OpenVINO Intel GPU"
    const name = plugin ? `Intel ${plugin.replace("intel:", "").toUpperCase()}` : null;
    return {
      label: name ? `OpenVINO ${name}` : "OpenVINO",
      gpu: !!plugin && !plugin.endsWith("cpu"),
    };
  }
  const kind = rt.accelerator ?? "cpu";
  return { label: ACCELERATOR[kind] ?? kind, gpu: kind !== "cpu" };
}

/** Where InsightFace (gender/age, staff faces) runs. */
export function facesBackend(rt: RunRuntime, faces = true): Backend {
  if (!faces || rt.faces_backend === "off") return { label: "off", gpu: false };
  const provider = rt.faces_backend ?? "CPUExecutionProvider";
  const label = PROVIDER[provider] ?? provider.replace("ExecutionProvider", "");
  return { label, gpu: label !== "CPU" };
}

/** How video is decoded. */
export function decodeBackend(rt: RunRuntime): Backend {
  if (rt.decoder !== "ffmpeg") return { label: "OpenCV, software", gpu: false };
  if (!rt.hwaccel || rt.hwaccel === "none") return { label: "ffmpeg, software", gpu: false };
  return {
    label: `ffmpeg on ${HWACCEL[rt.hwaccel] ?? rt.hwaccel}`,
    // Only a method that really decoded a frame here counts as on the GPU.
    gpu: rt.hw_decode !== false,
  };
}

/** One line for the run history: "CUDA · faces CUDA · NVDEC (NVIDIA)". */
export function runtimeSummary(rt: RunRuntime): string {
  const faces = facesBackend(rt, rt.faces ?? true);
  return [
    detectionBackend(rt).label,
    `faces ${faces.label}`,
    decodeBackend(rt).label.replace("ffmpeg on ", ""),
  ].join(" · ");
}
